/**
 * NxCapasArquitectura -- la grilla capa x repo en la Sala de Maquinas (Josse "va con las capas", Ares #38145, paso 4).
 *
 * AUDIENCIA: el equipo Nexo (#/salud ya es audiencia nexo), en el PC y en el celular. Una fila por capa: el numero de
 * cada repo en gris (no hay meta aprobada) y, al tocar, la lista que lo forma, como se midio y, si falta, por que.
 * Va aparte del tablero, como los chequeos: si una falla, la otra se pinta igual. Las reglas, en
 * application/salud/capasArquitectura.js.
 *
 * @module presentation/shared/NxCapasArquitectura
 */
import { SaludApiClient } from '../../infrastructure/api/SaludApiClient.js';
import { AppConfig } from '../../config.js';
import { grillaDeCapas, porQueNoHay } from '../../application/salud/capasArquitectura.js';
import { esc } from './texto.js';

const C = { texto: '#e6edf3', suave: '#8b949e', linea: '#30363d', fondo: '#161b22', gris: '#8b949e', enlace: '#58a6ff' };

function celdaAbierta(repo, celda) {
  const cabeza = `<div style="display:flex;flex-wrap:wrap;gap:2px 10px;align-items:baseline">
      <strong style="flex:1 1 140px;min-width:0">${esc(repo.nombre)}</strong>
      <span data-capa-valor style="color:${C.gris};font-weight:700">${esc(celda.texto)}</span></div>`;
  if (celda.motivo) return `<div data-capa-repo="${esc(repo.repo)}" style="padding:8px 0;border-top:1px solid ${C.linea}">${cabeza}<div data-capa-motivo style="color:${C.suave}">Sin medir: ${esc(celda.motivo)}</div></div>`;
  const lista = celda.visibles.length
    ? `<ul data-capa-lista style="margin:4px 0 0;padding-left:18px">${celda.visibles.map((t) => `<li style="overflow-wrap:anywhere">${esc(t)}</li>`).join('')}</ul>`
      + (celda.resto ? `<div data-capa-resto style="color:${C.suave}">y ${celda.resto} más</div>` : '')
    : `<div data-capa-lista-vacia style="color:${C.suave}">La lista está vacía.</div>`;
  const como = celda.como ? `<div data-capa-como style="margin-top:4px;color:${C.suave};overflow-wrap:anywhere">Cómo se midió: ${esc(celda.como)}</div>` : '';
  return `<div data-capa-repo="${esc(repo.repo)}" style="padding:8px 0;border-top:1px solid ${C.linea}">${cabeza}${lista}${como}</div>`;
}

function filaHtml(fila, repos) {
  const resumen = fila.celdas.map((c, i) => `<span style="white-space:nowrap"><span style="color:${C.suave}">${esc(repos[i].nombre)}</span> <span data-capa-celda="${esc(c.repo)}" style="color:${C.gris};font-weight:700">${esc(c.texto)}</span></span>`).join('');
  return `<details data-capa="${esc(fila.clave)}" style="padding:8px 0;border-top:1px solid ${C.linea}">
      <summary><strong>Capa ${fila.capa} · ${esc(fila.nombre)}</strong>
        <span style="display:flex;flex-wrap:wrap;gap:2px 14px;margin-top:2px">${resumen}</span></summary>
      <div style="margin-top:4px;color:${C.suave}">${esc(fila.frase)}</div>
      ${fila.celdas.map((c, i) => celdaAbierta(repos[i], c)).join('')}
    </details>`;
}

// Una linea por repo que si se puede partir: con nowrap, la del panel (con su fuente) empujaba la pagina a 391 en 375.
function pie(repos) {
  return repos.map((r) => `<div data-capa-pie="${esc(r.repo)}" style="overflow-wrap:anywhere">${esc(r.nombre)}: ${r.publico
    ? `medido ${esc(r.fecha || 'sin fecha')}${r.sha ? ' @' + esc(r.sha) : ''}${r.fuente ? ' · ' + esc(r.fuente) : ''}`
    : 'sin foto'}</div>`).join('');
}

function marco(cuerpo) {
  return `<style>
      [data-capas] summary { list-style: none; cursor: pointer; }
      [data-capas] summary::-webkit-details-marker { display: none; }
      [data-capas] summary > strong::before { content: '\\25B8  '; color: ${C.suave}; }
      [data-capas] details[open] > summary > strong::before { content: '\\25BE  '; }
      [data-capas] summary:focus-visible { outline: 2px solid ${C.enlace}; outline-offset: 2px; }
    </style>
    <section data-capas style="margin-top:14px;padding:12px 14px;border:1px solid ${C.linea};border-left:4px solid ${C.gris};border-radius:10px;background:${C.fondo};font-size:14px;color:${C.texto}">
      <div style="font-weight:700">Capas de la arquitectura</div>
      <div style="color:${C.suave}">Sin meta aprobada todavía: cada número va en gris con su lista. Las metas las fija Josse.</div>
      ${cuerpo}
    </section>`;
}

/** Lo pintado, puro sobre lo leido (para el guardia). r = la respuesta de SaludApiClient.getCapas(). */
export function capasHtml(r, env = 'prod') {
  if (!r?.ok) return marco(`<div data-capas-motivo="${esc(r?.motivo || 'ERROR_API')}" style="margin-top:8px;color:${C.suave}">${esc(porQueNoHay(r?.motivo, env))}</div>`);
  const g = grillaDeCapas(r.data);
  if (!g.publicaron) return marco(`<div data-capas-motivo="VACIO" style="margin-top:8px;color:${C.suave}">${esc(porQueNoHay('VACIO', env))}</div>`);
  return marco(`<div style="margin-top:6px">${g.filas.map((f) => filaHtml(f, g.repos)).join('')}</div>
      <div data-capas-pie style="margin-top:8px;color:${C.suave};font-size:14px">${pie(g.repos)}</div>`);
}

/**
 * Monta la grilla en el host. pedida = la respuesta (o su promesa) que ya pidio la vista para el dial (Ares #38227):
 * una sola peticion para los dos. @returns {Promise<boolean>} si hubo fotos.
 */
export async function renderCapasArquitectura($host, pedida = null) {
  if (!$host) return false;
  const r = await (pedida ?? SaludApiClient.getCapas());
  if (!$host.isConnected) return false;
  $host.innerHTML = capasHtml(r, r.ok ? 'prod' : AppConfig.env);
  return !!(r.ok && grillaDeCapas(r.data).publicaron);
}
